import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, Stripe ,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";

import { StatusCode } from "api/protocols";
import {
  GetPaymentMethodRequest,
  IPaymentMethodRequestInfo,
  PostGuestPaymentMethod,
  PostGuestPaymentMethodSetup,
} from "api/rpc/2024-04/guest/paymentMethod";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import CardSection from "components/cardSection/CardSection";
import { ButtonNew as Button } from "components/buttonNew";
import Callout from "components/callout/Callout";
import Spin from "components/spin/spin";

import "./PaymentMethodCollect.scss";

type PageStatus = "loading" | "ready" | "invalid" | "success";

function PaymentMethodCollectForm(props: {
  token: string;
  requestInfo: IPaymentMethodRequestInfo;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [elementComplete, setElementComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });

  function handleCardInputChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setElementComplete(prev => ({
      ...prev,
      [e.elementType]: e.complete,
    }));
  }

  async function handleSubmit() {
    if (!stripe || !elements || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
        billing_details: {
          name: props.requestInfo.customer.full_name,
          email: props.requestInfo.customer.email,
          phone: props.requestInfo.customer.phone,
          address: {
            state: props.requestInfo.customer.state,
          },
        },
      });

      if (stripeError) {
        dispatch(showError(stripeError.message));
        setSubmitting(false);
        return;
      }

      const setupRes = await PostGuestPaymentMethodSetup({ token: props.token }, true);
      if (setupRes.status !== StatusCode.OK) {
        dispatch(showError(setupRes.message || t("Unable to set up payment method")));
        setSubmitting(false);
        return;
      }

      const stripeConfirmRes = await stripe.confirmCardSetup(setupRes.data?.setup_intent?.client_secret as string, {
        payment_method: paymentMethod.id,
      });

      if (stripeConfirmRes.error) {
        dispatch(showError(stripeConfirmRes.error.message));
        setSubmitting(false);
        return;
      }

      const paymentMethodRes = await PostGuestPaymentMethod(
        {
          token: props.token,
          payment_method_id: stripeConfirmRes.setupIntent.payment_method,
          save_card: true,
          save_on_profile: true,
        },
        true,
      );

      if (paymentMethodRes.status !== StatusCode.OK) {
        dispatch(showError(paymentMethodRes.message || t("Unable to save payment method")));
        setSubmitting(false);
        return;
      }

      dispatch(showSuccess(paymentMethodRes.message || t("Payment method added successfully")));
      props.onSuccess();
    } catch (e) {
      console.log(e);
      dispatch(showError(t("Unable to save payment method")));
    } finally {
      setSubmitting(false);
    }
  }

  const formReady = elementComplete.cardNumber && elementComplete.cardExpiry && elementComplete.cardCvc;

  return (
    <div className="payment-method-collect-form">
      <label className="payment-method-collect-label">{t("Enter Credit Card Information")}</label>
      <CardSection type="two-row" onChange={handleCardInputChange} />
      <div className="payment-method-collect-actions">
        <Button type="primary" size="medium" onClick={handleSubmit} disabled={!formReady || submitting || !stripe}>
          {t("Save Payment Method")}
        </Button>
      </div>
    </div>
  );
}

export default function PaymentMethodCollect() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const token = urlParams.get("token");

  const [pageStatus, setPageStatus] = useState<PageStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [requestInfo, setRequestInfo] = useState<IPaymentMethodRequestInfo>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    void loadRequest();
  }, []);

  async function loadRequest() {
    if (!token) {
      setErrorMessage(t("This payment method link is invalid."));
      setPageStatus("invalid");
      return;
    }

    const res = await GetPaymentMethodRequest({ token }, true);
    if (res.status !== StatusCode.OK || !res.data) {
      setErrorMessage(res.message || t("This payment method link is invalid or has expired."));
      setPageStatus("invalid");
      return;
    }

    const info = res.data as IPaymentMethodRequestInfo;
    setRequestInfo(info);

    let stripeKey = process.env.REACT_APP_STRIPE_PUBLISH_KEY;
    if (info.facility?.country_code === "US") {
      stripeKey = process.env.REACT_APP_STRIPE_PUBLISH_KEY_US;
    }

    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    } else {
      dispatch(showError(t("Unable to load payment form")));
      setErrorMessage(t("Unable to load payment form"));
      setPageStatus("invalid");
      return;
    }

    setPageStatus("ready");
  }

  return (
    <div className="payment-method-collect">
      <div className="payment-method-collect-card">
        {requestInfo?.facility?.logo_source ? (
          <div className="payment-method-collect-logo">
            <img src={requestInfo.facility.logo_source} alt={requestInfo.facility.long_name} />
          </div>
        ) : null}

        {pageStatus === "loading" && (
          <div className="payment-method-collect-loading">
            <Spin />
          </div>
        )}

        {pageStatus === "invalid" && (
          <Callout
            type="error"
            title={t("Link unavailable")}
            content={errorMessage || t("This payment method link is invalid or has expired.")}
          />
        )}

        {pageStatus === "success" && requestInfo && (
          <Callout
            type="success"
            title={t("Payment method saved")}
            content={t("Thank you. Your payment method has been securely added to your account.")}
          />
        )}

        {pageStatus === "ready" && requestInfo && (
          <>
            <h1 className="payment-method-collect-title">{t("Add Payment Method")}</h1>
            <p className="payment-method-collect-subtitle">
              {t("Hi {{name}}, {{facility}} has requested that you securely add a payment method to your account.", {
                name: requestInfo.customer.first_name,
                facility: requestInfo.facility.long_name || requestInfo.client.long_name,
              })}
            </p>
            <Elements stripe={stripePromise}>
              <PaymentMethodCollectForm
                token={token}
                requestInfo={requestInfo}
                onSuccess={() => setPageStatus("success")}
              />
            </Elements>
          </>
        )}
      </div>
    </div>
  );
}
